import { type ZodSchema, z } from "zod";
import { PhoneCallDirectionSchema } from "@remi/enums/phone-call-direction";
import { SocketSuggestedActionType } from "@remi/enums/socket/suggested-action-type";

import { NotificationListItemSchema } from "../notification/list";

const CacheMessageTypeValues = [
  "BID",
  "CONVERSATION",
  "TICKET",
  "RATEBOOK_IMPORT",
  "FULL",
  "LIVE_PHOTO_REVIEW",
  "CALENDAR_SYNC",
  "PROJECT_TASKS",
] as const;
const CacheMessageTypeSchema = z.enum(CacheMessageTypeValues);
export const CacheMessageType = CacheMessageTypeSchema.enum;
export type CacheMessageType = z.infer<typeof CacheMessageTypeSchema>;

const ConversationCacheMessageSchema = z.object({
  name: z.string(),
  type: z.literal(CacheMessageType.CONVERSATION),
  invalidateConversationId: z.string().optional(),
  invalidateConversationItemId: z.string().optional(),
  invalidateReplyParentId: z.string().optional(),
  invalidateReactionParentId: z.string().optional(),
  invalidateConversationMembersId: z.string().optional(),
  invalidateConversationSubscribersId: z.string().optional(),
});

const TicketCacheMessageSchema = z.object({
  name: z.string(),
  type: z.literal(CacheMessageType.TICKET),
  invalidateTicketId: z.string(),
});

const RatebookCacheMessageSchema = z.object({
  name: z.string(),
  type: z.literal(CacheMessageType.RATEBOOK_IMPORT),
  invalidateRatebookImportId: z.string(),
});

const FullCacheMessageSchema = z.object({
  name: z.string(),
  type: z.literal(CacheMessageType.FULL),
});

const LivePhotoReviewCacheMessageSchema = z.object({
  name: z.string(),
  type: z.literal(CacheMessageType.LIVE_PHOTO_REVIEW),
});

const CalendarSyncCacheMessageSchema = z.object({
  // `name` carries the contractorId; the channel name is derived as
  // `cache-calendar_sync:{name}` per packages/ably/src/emit.ts:55.
  name: z.string(),
  type: z.literal(CacheMessageType.CALENDAR_SYNC),
});

const BidCacheMessageSchema = z.object({
  name: z.string(),
  type: z.literal(CacheMessageType.BID),
});

const ProjectTasksCacheMessageSchema = z.object({
  // `name` carries the projectId as a string; the channel name is `cache-project_tasks:{name}`.
  name: z.string(),
  type: z.literal(CacheMessageType.PROJECT_TASKS),
  // All optional (the ordinary invalidation publish carries none) and Zod strips unknown keys, so any
  // attempt field missing from this schema is silently lost in transit. Loose strings, not enums: an
  // unrecognized value must degrade, not fail the arm's parse and drop the invalidation with it.
  attemptAbortReason: z.string().optional(),
  attemptDedupeKey: z.string().optional(),
  attemptPhase: z.string().optional(),
  completionNonce: z.string().optional(),
  completionPhase: z.string().optional(),
});

// The wire field above stays a loose string so an unrecognized phase degrades on old clients; this union
// is the producer-side lockstep, so a typo at a publish site fails tsc instead of emitting a phase no
// consumer can classify.
export type AttemptPhase = "committed" | "failed";

// Same loose-wire / strict-producer split as AttemptPhase. Ranked, not flat: `failed` can legitimately
// arrive after `committed` (the task IS complete, a tail step is not) and must not revert the UI.
export type CompletionPhase = "committed" | "evaluated" | "failed" | "raceLost" | "aborted";

export const CacheMessageSchema = z.discriminatedUnion("type", [
  BidCacheMessageSchema,
  ConversationCacheMessageSchema,
  TicketCacheMessageSchema,
  RatebookCacheMessageSchema,
  FullCacheMessageSchema,
  LivePhotoReviewCacheMessageSchema,
  CalendarSyncCacheMessageSchema,
  ProjectTasksCacheMessageSchema,
]);

export type CacheMessage = z.infer<typeof CacheMessageSchema>;
export type CacheMessageForType<T extends CacheMessageType> = Extract<CacheMessage, { type: T }>;

export const NotificationMessageSchema = z.object({
  name: z.string(),
  data: NotificationListItemSchema,
});

export type NotificationMessage = z.infer<typeof NotificationMessageSchema>;

const LiveCallSuggestedActionSchema = z.object({
  type: z.literal(SocketSuggestedActionType.LIVE_CALL),
  phoneCallId: z.string(),
  callerNumber: z.string(),
  callerName: z.string().nullable(),
  repPersonId: z.string(),
  direction: PhoneCallDirectionSchema,
  startedAt: z.string().nullable(),
  // Server-matched at ingest; null = unknown caller.
  matchedBidId: z.number().int().positive().nullable(),
  ended: z.boolean(),
});

export type LiveCallSuggestedAction = z.infer<typeof LiveCallSuggestedActionSchema>;

const SuggestedActionPayloadSchema = z.discriminatedUnion("type", [LiveCallSuggestedActionSchema]);

export type SuggestedActionPayload = z.infer<typeof SuggestedActionPayloadSchema>;

export const SuggestedActionMessageSchema = z.object({
  name: z.string(),
  data: SuggestedActionPayloadSchema,
});

export type SuggestedActionMessage = z.infer<typeof SuggestedActionMessageSchema>;

export const suggestedActionChannelName = (personId: string): string => `suggested-action-${personId}`;

export const CacheMessageSchemaByType: {
  [K in CacheMessageType]: ZodSchema<CacheMessageForType<K>>;
} = {
  [CacheMessageType.BID]: BidCacheMessageSchema,
  [CacheMessageType.CONVERSATION]: ConversationCacheMessageSchema,
  [CacheMessageType.TICKET]: TicketCacheMessageSchema,
  [CacheMessageType.RATEBOOK_IMPORT]: RatebookCacheMessageSchema,
  [CacheMessageType.FULL]: FullCacheMessageSchema,
  [CacheMessageType.LIVE_PHOTO_REVIEW]: LivePhotoReviewCacheMessageSchema,
  [CacheMessageType.CALENDAR_SYNC]: CalendarSyncCacheMessageSchema,
  [CacheMessageType.PROJECT_TASKS]: ProjectTasksCacheMessageSchema,
};

export type ChannelMessageCallback<T extends CacheMessageType> = (message: CacheMessageForType<T>) => void;
